import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ROUTES } from '@/app/routes';
import { safeMediaSrc } from '@/lib/mediaUrl';
import { formatCompact } from '@/lib/money';
import type { DashboardTopCreator } from '../api';

/**
 * Confluence §8 Top Creators — ranked by current follower count.
 * Avatars use S3-presigned URLs from the API (same pattern as Finance).
 */
export function DashboardTopCreatorsCard({
  creators,
  isLoading,
}: {
  creators: DashboardTopCreator[];
  isLoading?: boolean;
}) {
  return (
    <Card className="overflow-hidden border-border/40 bg-[#1a1a1c] shadow-none">
      <CardHeader className="space-y-1 p-4 pb-2">
        <CardTitle className="text-sm font-medium text-sky-400">Top Creators</CardTitle>
        <p className="text-[11px] text-muted-foreground">
          Creators who have published at least one post, ranked by current active follower count.
          Country filter uses Stripe onboarded country when applied.
        </p>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <p className="px-4 py-6 text-center text-xs text-muted-foreground">Loading…</p>
        ) : creators.length === 0 ? (
          <p className="px-4 py-6 text-center text-xs text-muted-foreground">
            No creators with followers yet.
          </p>
        ) : (
          <ul className="divide-y divide-border/40">
            {creators.map((creator, index) => {
              const name = creator.displayName || creator.username || 'Creator';
              const initial = name.charAt(0).toUpperCase();
              const id = creator.userId;
              const avatarSrc = safeMediaSrc(creator.avatarUrl);
              const row = (
                <span className="flex min-w-0 items-center gap-3">
                  <span className="w-4 shrink-0 text-xs tabular-nums text-muted-foreground">
                    {creator.rank ?? index + 1}
                  </span>
                  <Avatar className="size-9 shrink-0">
                    {avatarSrc ? <AvatarImage src={avatarSrc} alt={name} /> : null}
                    <AvatarFallback className="text-xs">{initial}</AvatarFallback>
                  </Avatar>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground">{name}</span>
                    {creator.username ? (
                      <span className="block truncate text-[11px] text-muted-foreground">
                        @{creator.username}
                      </span>
                    ) : null}
                  </span>
                </span>
              );
              return (
                <li
                  key={id ?? `${name}-${index}`}
                  className="flex items-center justify-between gap-3 px-4 py-3 text-xs"
                >
                  {id != null ? (
                    <Link
                      to={ROUTES.userDetails(String(id))}
                      className="min-w-0 text-inherit hover:text-sky-300"
                    >
                      {row}
                    </Link>
                  ) : (
                    row
                  )}
                  <span className="shrink-0 tabular-nums text-sm font-medium text-foreground">
                    {formatCompact(creator.followerCount ?? 0)}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
